import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import Loading from "../../Components/Loading/Loading";
import Util from "../../Components/ShowCard/util";
import { fetchActivity } from "./service";
import { Link, useNavigate } from "react-router-dom";
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { ReactComponent as ChevronIcon } from "../../assets/Icons/chevron-down.svg";

const MyActivity = () => {
    const projectInfo = useSelector((state) => state?.projectInfo?.value);
    const accessToken = useSelector((state) => state?.accessToken?.value);
    const user = useSelector((state) => state?.user?.value);

    const getDefaultDateRange = () => {
        const today = new Date();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(today.getDate() - 29);
        return [thirtyDaysAgo, today];
    };

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState(getDefaultDateRange());
    const [completionFilter, setCompletionFilter] = useState('All');
    const [showCalendar, setShowCalendar] = useState(false);
    const [showCompletionDropdown, setShowCompletionDropdown] = useState(false);

    const calendarRef = useRef(null);
    const completionRef = useRef(null);
    const navigate = useNavigate();

    const appInfo = {
        projectDetails: projectInfo,
        accessToken: accessToken,
    };

    useEffect(() => {
        window.scroll(0, 0);
    }, []);

    useEffect(() => {
        fetchUserActivity();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setShowCalendar(false);
            }
            if (completionRef.current && !completionRef.current.contains(event.target)) {
                setShowCompletionDropdown(false);
            }
        };

        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                setShowCalendar(false);
                setShowCompletionDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscapeKey);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, []);

    const fetchUserActivity = async (startDate = null, endDate = null) => {
        if (!user) {
            setLoading(false);
            return;
        }
        try {
            const params = {};
            if (startDate && endDate) {
                params.start_date = formatDateForApi(startDate);
                params.end_date = formatDateForApi(endDate);
            }
            const response = await fetchActivity(appInfo, params);
            if (response?.status === 200) {
                const responseData = response?.data?.data || [];
                setData(responseData);
                applyCompletionFilter(responseData, completionFilter);
            }
        } catch (err) {
        } finally {
            setLoading(false);
        }
    };

    const formatDateForApi = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const applyCompletionFilter = (data, filter) => {
        let filtered = data;
        if (filter === 'Completed') {
            filtered = data.map(dateGroup => ({
                ...dateGroup,
                videos: dateGroup.videos.filter(video => video.completed === true)
            })).filter(dateGroup => dateGroup.videos.length > 0);
        } else if (filter === 'Incomplete') {
            filtered = data.map(dateGroup => ({
                ...dateGroup,
                videos: dateGroup.videos.filter(video => video.completed === false)
            })).filter(dateGroup => dateGroup.videos.length > 0);
        }
        setFilteredData(filtered);
    };

    useEffect(() => {
        fetchUserActivity(dateRange[0], dateRange[1]);
    }, [dateRange]);

    useEffect(() => {
        applyCompletionFilter(data, completionFilter);
    }, [data, completionFilter]);

    const handleDateRangeChange = (newDateRange) => {
        setDateRange(newDateRange);
        setShowCalendar(false);
    };

    const handleCompletionFilterChange = (filter) => {
        setCompletionFilter(filter);
        setShowCompletionDropdown(false);
    };

    const { replaceImage } = Util();

    const formatDateRange = (startDate, endDate) => {
        const options = { month: 'short', day: 'numeric' };
        const start = startDate.toLocaleDateString(undefined, options);
        const end = endDate.toLocaleDateString(undefined, options);
        if (startDate.getFullYear() !== endDate.getFullYear()) {
            return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
        }
        return `${start} - ${end}, ${endDate.getFullYear()}`;
    };

    if (loading) return <Loading />;

    if (!user) {
        return (
            <div className="myActivity">
                <div className="wrapper">
                    <div className="top">
                        <h1 className={`heading`}>My Activity</h1>
                    </div>
                    <div className="bottom">
                        <div className="emptyState">
                            <p>You need to sign in to view your activity.</p>
                            <Link to="/login">Sign in</Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="myActivity">
            <div className="wrapper">
                <div className="top">
                    <div className="headerRow">
                        <h1 className="heading">My Activity</h1>
                        <div className="filters-container">
                            <div className="filters-content">
                                <div className="filters-grid">
                                    <div className="custom-dropdown" ref={calendarRef}>
                                        <div
                                            className={`dropdown-selector ${showCalendar ? 'active' : ''}`}
                                            onClick={() => setShowCalendar(!showCalendar)}
                                            aria-expanded={showCalendar}
                                            aria-haspopup="true"
                                        >
                                            <span className="selected-value">{formatDateRange(dateRange[0], dateRange[1])}</span>
                                            <ChevronIcon className={`chevron-icon ${showCalendar ? 'open' : ''}`} />
                                        </div>
                                        {showCalendar && (
                                            <div className="calendarDropdown">
                                                <div className="calendarHeader">
                                                    <span className="calendarTitle">Select Date Range</span>
                                                    <button
                                                        className="closeButton"
                                                        onClick={() => setShowCalendar(false)}
                                                        aria-label="Close calendar"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                                <Calendar
                                                    onChange={handleDateRangeChange}
                                                    value={dateRange}
                                                    selectRange={true}
                                                    maxDate={new Date()}
                                                    showNeighboringMonth={false}
                                                    prev2Label={null}
                                                    next2Label={null}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div className="custom-dropdown" ref={completionRef}>
                                        <div
                                            className={`dropdown-selector ${showCompletionDropdown ? 'active' : ''}`}
                                            onClick={() => setShowCompletionDropdown(!showCompletionDropdown)}
                                            aria-expanded={showCompletionDropdown}
                                            aria-haspopup="true"
                                        >
                                            <span className="selected-value">{completionFilter}</span>
                                            <ChevronIcon className={`chevron-icon ${showCompletionDropdown ? 'open' : ''}`} />
                                        </div>
                                        {showCompletionDropdown && (
                                            <div className="dropdown-menu">
                                                <div
                                                    className={`dropdown-item ${completionFilter === 'All' ? 'selected' : ''}`}
                                                    onClick={() => handleCompletionFilterChange('All')}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleCompletionFilterChange('All')}
                                                >
                                                    All
                                                </div>
                                                <div
                                                    className={`dropdown-item ${completionFilter === 'Completed' ? 'selected' : ''}`}
                                                    onClick={() => handleCompletionFilterChange('Completed')}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleCompletionFilterChange('Completed')}
                                                >
                                                    Completed
                                                </div>
                                                <div
                                                    className={`dropdown-item ${completionFilter === 'Incomplete' ? 'selected' : ''}`}
                                                    onClick={() => handleCompletionFilterChange('Incomplete')}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleCompletionFilterChange('Incomplete')}
                                                >
                                                    Incomplete
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="bottom">
                    <div className="itemsContainer activityList">
                        {filteredData.map((dateGroup, groupIndex) => (
                            <div className="dateGroup" key={groupIndex}>
                                <h2 className="dateHeading">{dateGroup.date_heading}</h2>
                                <div className="dateItems">
                                    {dateGroup.videos.map((video, videoIndex) => (
                                        <div key={videoIndex} className="activityCard">
                                            <div className="thumb" onClick={() => navigate(`/videos/${video?.video_vanity_url}`)}>
                                                <img
                                                    src={video?.thumbnail || (process.env.REACT_APP_IMAGE_URL + video?.thumbnail_350_200)}
                                                    alt={video?.title}
                                                    onError={(e) => replaceImage(e, "LANDSCAPE", projectInfo)}
                                                />
                                                <div className="playOverlay" aria-hidden="true">
                                                    <span className="playButton" />
                                                </div>
                                                {video?.completed && (
                                                    <span className="watchedBadge">✓</span>
                                                )}
                                                {video?.watched_percentage > 0 && !video?.completed && (
                                                    <div className="progressOverlay">
                                                        <div className="progressBar">
                                                            <div className="progress" style={{ width: `${video?.watched_percentage || 0}%` }} aria-label={`Progress: ${video?.watched_percentage || 0}%`} />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="info">
                                                <div className="titleRow">
                                                    <h3 className="title">{video?.title}</h3>
                                                </div>
                                                <p className="description">{video?.description}</p>
                                                <div className="metaRow">
                                                    <span className="duration">{video?.duration_text}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                        {filteredData?.length === 0 && (
                            <div className="emptyState">
                                <p>No activity found for the selected criteria.</p>
                                <Link to="/home">Browse videos</Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MyActivity;
